import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { fmtPts } from "@/components/thespian/ThespianRankCard";
import ActivityLabel from "@/components/thespian/ActivityLabel";
import { formatDateOnly } from "@/lib/date";

const canEdit = (c) => c.status === "pending" || c.status === "returned";
const editLabel = (c) => (c.status === "returned" ? "Fix & resubmit" : "Edit");

function Points({ c }) {
  if (c.status !== "approved") return <span className="font-bold tabular-nums text-amber-700">{fmtPts(c.points_claimed)}</span>;
  return (
    <span className="font-bold tabular-nums text-emerald-700" data-testid={`thespian-awarded-${c.id}`}>
      {fmtPts(c.points_awarded)}
      {c.points_edited && <span className="ml-1 text-[11px] font-medium text-slate-400 line-through">{fmtPts(c.points_claimed)}</span>}
    </span>
  );
}

function Feedback({ c }) {
  const note = c.status === "returned" ? c.return_note : c.review_note;
  return note ? <p className="mt-1 text-xs text-violet-700" data-testid={`thespian-feedback-${c.id}`}>Director: {note}</p> : null;
}

function ClaimCard({ c, onEdit }) {
  return (
    <Card className="border-slate-200 p-4" data-testid={`thespian-claim-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <ActivityLabel c={c} />
        <Points c={c} />
      </div>
      <p className="mt-1 text-xs text-slate-500">{formatDateOnly(c.activity_date)} · {c.notes}</p>
      <div className="mt-2"><StatusBadge status={c.status} /></div>
      <Feedback c={c} />
      {onEdit && canEdit(c) && <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={() => onEdit(c)}>{editLabel(c)}</Button>}
    </Card>
  );
}

// Claims table (desktop) + cards (mobile). Read-only when `onEdit` is omitted (parent view).
export default function ThespianClaimsList({ items, onEdit }) {
  const heads = ["Date", "Activity", "Points", "Documentation", "Status", ...(onEdit ? [""] : [])];
  return (
    <>
      <Card className="hidden overflow-hidden border-slate-200 shadow-sm md:block">
        <Table data-testid="thespian-claims-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              {heads.map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`thespian-claim-row-${c.id}`}>
                <TableCell className="whitespace-nowrap text-sm tabular-nums">{formatDateOnly(c.activity_date)}</TableCell>
                <TableCell className="max-w-xs"><ActivityLabel c={c} /></TableCell>
                <TableCell><Points c={c} /></TableCell>
                <TableCell className="max-w-xs text-sm text-slate-600">{c.notes}</TableCell>
                <TableCell className="max-w-[14rem]"><StatusBadge status={c.status} /><Feedback c={c} /></TableCell>
                {onEdit && <TableCell>{canEdit(c) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(c)} data-testid={`thespian-edit-${c.id}`}>{editLabel(c)}</Button>}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <div className="space-y-3 md:hidden" data-testid="thespian-claim-cards">
        {items.map((c) => <ClaimCard key={c.id} c={c} onEdit={onEdit} />)}
      </div>
    </>
  );
}
